<template>
   <div class="w-full flex flex-col space-y-2">
       <div class="">
           <div ref="allEmails" class="flex flex-wrap space-x-1.5 items-center relative w-full text-lg p-2 px-3 rounded-lg border border-gray-500" role="textbox">
               <div v-if="form.emails.length" v-for="(email, index) in form.emails">
                   <div v-if="email.valid" class="inline-flex items-center space-x-1 bg-regal-blue-100 text-regal-blue-700 px-2 rounded-lg">
                       <div class="">
                           {{ email.value }}
                       </div>
                       <div class="w-5 h-full flex items-center justify-center hover:text-regal-blue-500 cursor-pointer" @click="deleteEmail(index)">
                           <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path></svg>
                       </div>
                   </div>
                   <div v-if="!email.valid" class="inline-flex items-center space-x-1 bg-white text-gray-800 border border-dashed border-red-600 px-1.5 -mx-[1px] -my-[1px] rounded-lg">
                       <div class="">
                           {{ email.value }}
                       </div>
                       <div class="w-5 h-full flex items-center justify-center hover:text-regal-blue-500 cursor-pointer" @click="deleteEmail(index)">
                           <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path></svg>
                       </div>
                   </div>
               </div>
               <div class="inline-flex outline-none flex-1 cursor-text" @input="onInput" @paste="onPaste" @blur="emailInputFocused = false" ref="emailInput" contenteditable="" />
           </div>
       </div>
       <div v-if="invalidEmails.length == 0" class="text-sm font-medium text-gray-800 flex space-x-1">
           <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
           <div class="flex items-center justify-between w-full">
           		<div>Separate multiple email addresses with a [comma] or [space]</div>
           		<div class="text-gray-700">{{ form.emails.length }}/50</div>
           </div>
       </div>
       <div v-else class="text-sm font-medium text-gray-800 flex space-x-1">
           <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
           <div class="">
                {{ invalidEmails.length }} invalid email(s). <span @click="clearInvalidEmails" class="text-regal-blue-700 underline hover:no-underline cursor-pointer">Remove all emails with errors.</span>
           </div>
       </div>
       <InlineError v-if="form.errors.emails" :message="form.errors.emails"/>
   </div>
</template>

<script setup>
import { useForm } from '@inertiajs/inertia-vue3'
import { onMounted, onUnmounted, computed, defineProps, defineEmits, ref } from 'vue'
import InlineError from '@/Shared/InlineError'

const emit = defineEmits(['update:form'])
const props = defineProps(['form'])

const emailInputFocused = ref(false)
const emailInput = ref(null)

onMounted(() => {
	document.addEventListener('focusin', focusChanged)
	document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
	document.removeEventListener('focusin', focusChanged)
	document.removeEventListener('keydown', onKeydown)
})

const onKeydown = (e) => {
    if (e.keyCode === 8 || e.keyCode === 46) {
        onBackspace()
    }
}
const focusChanged = (e) => {
    if (e.target === emailInput.value) {
        emailInputFocused.value = true
    } else {
        emailInputFocused.value = false
    }
}

const invalidEmails = computed(() => {
	return props.form.emails.filter(email => email.valid === false)
})
const onInput = () => {
    const input = emailInput.value
    const lastChar = input.textContent.slice(-1);
    if (/\s/g.test(input.textContent) || lastChar == ',') {
        if (isEmail(input.textContent.slice(0, -1)) && input.textContent.slice(0, -1)) {
            let email = isEmail(input.textContent.slice(0, -1))[0]
            props.form.emails.push({ value: email, valid: true })
            input.textContent = ''
            input.focus()
        } else {
            props.form.emails.push({ value: input.textContent.slice(0, -1), valid: false})
            input.textContent = ''
            input.focus()
        }
    }
    props.form.incompleteEmail = input.textContent
    emit('update:form', props.form)
}

const onPaste = (e) => {
    e.stopPropagation()
    e.preventDefault()
    let clipboardData = e.clipboardData || window.clipboardData
    let pastedData = clipboardData.getData('text/plain')
    let splitArray = pastedData.split(/[\s,]+/)
    let emailArray = []
    const input = emailInput.value

    splitArray.forEach((email, i) => {
        if (isEmail(email)) {
            props.form.emails.push({ value: email, valid: true })
            input.textContent = ''
        } else {
            props.form.emails.push({ value: email, valid: false })
            input.textContent = ''
        }
    })
    emit('update:form', props.form)
}

const isEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
}

const deleteEmail = (index) => {
    props.form.emails.splice(index, 1)
    emailInput.value.focus()
    emit('update:form', props.form)
}

const clearInvalidEmails = () => {
    props.form.emails = props.form.emails.filter(email => email.valid)
    emit('update:form', props.form)
}

const onBackspace = () => {
	if (emailInputFocused.value && emailInput.value.textContent == '') {
	    if (props.form.emails.length > 0) {
	        props.form.emails.pop()
	        return
	    }
	}
	emit('update:form', props.form)
}
	
</script>